import { useId } from "react";

/**
 * Shared chart controls. Options come from the caller (usually an API
 * registry), so charts follow server-side definitions instead of
 * hard-coding them.
 */

/**
 * Row of toggle buttons for picking one option.
 * @param {string} label - Accessible group label
 * @param {Array<{value: string, label: string}>} options
 * @param {string} value - Selected option value
 * @param {function} onChange - Called with the new value
 */
export const SegmentedControl = ({ label, options, value, onChange }) => (
	<div
		role="group"
		aria-label={label}
		className="inline-flex flex-wrap overflow-hidden rounded-md border border-gray-300"
	>
		{options.map((option) => {
			const active = option.value === value;
			return (
				<button
					key={option.value}
					type="button"
					aria-pressed={active}
					onClick={() => onChange(option.value)}
					className={`min-h-[44px] border-l border-gray-300 px-3 text-sm font-semibold transition-colors duration-200 first:border-l-0 ${
						active ? "bg-gray-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
					}`}
				>
					{option.label}
				</button>
			);
		})}
	</div>
);

const GRAIN_LABELS = { day: "Day", week: "Week", month: "Month", year: "Year" };

/**
 * Time grain picker (day / week / month / year).
 * @param {Array<string>} grains - Grain keys, e.g. from /steam/insights/metrics
 */
export const GrainSelector = ({ grains = Object.keys(GRAIN_LABELS), value, onChange }) => (
	<SegmentedControl
		label="Time grain"
		options={grains.map((grain) => ({ value: grain, label: GRAIN_LABELS[grain] || grain }))}
		value={value}
		onChange={onChange}
	/>
);

/**
 * Metric picker with the selected metric's definition shown underneath.
 * @param {Array<{key: string, label: string, description: string}>} metrics
 */
export const MetricSelector = ({ metrics = [], value, onChange }) => {
	const id = useId();
	const selected = metrics.find((metric) => metric.key === value);
	return (
		<div className="w-full sm:w-auto">
			<label htmlFor={id} className="mb-1 block text-sm text-gray-500">
				Metric
			</label>
			<select
				id={id}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-800 sm:w-auto"
			>
				{metrics.map((metric) => (
					<option key={metric.key} value={metric.key}>
						{metric.label}
					</option>
				))}
			</select>
			{selected && <p className="mt-2 max-w-prose text-sm text-gray-500">{selected.description}</p>}
		</div>
	);
};
